import { z } from 'zod';
import { chatPublicActivityEntrySchema } from '../ai/chatActivity';
import { chatToolResultSchema } from '../ai/chatToolResults';
import { Channel } from '.';

const AiChatChannelSchemas = {
  message: z.object({
    content: z.string(),
    checksum: z.string().optional(),
    role: z.enum(['USER', 'ASSISTANT', 'SYSTEM']),
    createdAt: z.string(),
    metadata: z.any().optional(),
    messageId: z.number().optional(),
  }),
  thinking: z.object({ message: z.string(), activity: chatPublicActivityEntrySchema.optional() }),
  messageStream: z.object({
    type: z.enum(['delta', 'finish']),
    delta: z.string().optional(),
    triggerMessageId: z.number().optional(),
  }),
  toolCall: z.object({
    toolCallId: z.string(),
    toolName: z.string(),
    message: z.string(),
    agentId: z.number(), // Allocation ID for per-agent tracking
    agentTask: z.string(), // User-friendly task name, e.g. "Creating sales order"
  }),
  toolResult: z.object({
    toolCallId: z.string(),
    toolName: z.string(),
    agentId: z.number().optional(),
    publicResult: chatToolResultSchema.nullable().optional(),
  }),
  status: z.object({
    triggerMessageId: z.number().optional(),
    activity: chatPublicActivityEntrySchema.optional(),
    groupId: z.string(), // e.g., "agent-42", "thinking", "document-123"
    type: z.enum(['start', 'complete']),
    message: z.string().optional(), // Display message (required for 'start')
    label: z.string().optional(), // Optional label like agent task name
  }),
  /**
   * Title update event - sent when the narrator updates the conversation title
   */
  titleUpdate: z.object({ title: z.string() }),
} as const;

export type AiChatTopicData = {
  [K in keyof typeof AiChatChannelSchemas]: z.infer<(typeof AiChatChannelSchemas)[K]>;
};

export const AiChatChannel: Channel<{ conversationId: number }, typeof AiChatChannelSchemas> = {
  getChannelId: (params) => `ai_chat_${params.conversationId}`,
  topics: {
    message: 'message',
    thinking: 'thinking',
    messageStream: 'messageStream',
    toolCall: 'toolCall',
    toolResult: 'toolResult',
    status: 'status',
    titleUpdate: 'titleUpdate',
  },
  schemas: AiChatChannelSchemas,
};
